import NStatusChip from "@/components/common/NStatusChip";
import useAuth from "@/lib/auth/useAuth";
import { formatDateStringMMDDYYYY } from "@/lib/utils/date";
import {
    getEnrollmentTierLabel,
    EnrollmentTier,
    TIER_OPTIONS,
} from "@/lib/utils/enrollment";
import { Field } from "@/types/field";
import {
    faChevronLeft,
    faPlus,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Card,
    CardFooter,
    CardHeader,
    Chip,
    Divider,
    Input,
    Skeleton,
    Spinner,
} from "@heroui/react";
import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useInfiniteQuery } from "@tanstack/react-query";
import { sortArrayByKey } from "@/lib/utils/sorting";
import useLayout from "@/lib/layout/useLayout";
import useIsMobile from "@/lib/utils/useIsMobile";
import useRequest from "@/lib/request/useRequest";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import ApplyNChip from "@/components/common/ApplyNChip";
import DoNotApplyNChip from "@/components/common/DoNotApplyNChip";
import { ViewType } from "@/types/enums";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { PaginationResponse } from "@/types/pagination";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";

const searchSchema = z.object({
    search: z.string().optional(),
    service_provider: z.string().optional(),
    grower: z.string().optional(),
    account_manager: z.string().optional(),
    enrollment_tier: z.string().optional(),
});

export const Route = createFileRoute("/dashboard/fields/")({
    component: RouteComponent,
    validateSearch: zodValidator(searchSchema),
});

function FooterContent({ field }: { field: Field }) {
    const { t } = useTranslation("fields-list");
    const { user, userIsSentinel, isGrower } = useAuth();
    const insightsEligible = useMemo(() => {
        const userInList = field.users.some((u) => u.user_id === user?.id);
        const hasNonTrialView = field.users.some(
            (u) => u.user_id === user?.id && u.view_type !== ViewType.Trial,
        );
        return (
            userIsSentinel ||
            (isGrower && (!userInList || hasNonTrialView)) ||
            (!isGrower && hasNonTrialView)
        );
    }, [field.users, isGrower, user?.id, userIsSentinel]);

    if (
        !field.recommendation ||
        !insightsEligible ||
        field.active_enrollment?.enrollment_tier === EnrollmentTier.Irrigation
    )
        return null;

    if (
        field.recommendation.user_recommendation != null &&
        field.recommendation.user_recommendation.recommend_application != null
    ) {
        return (
            <div className="flex gap-1">
                <p>
                    {field.recommendation.user_recommendation
                        .recommend_application ? (
                        <ApplyNChip />
                    ) : (
                        <DoNotApplyNChip />
                    )}
                </p>
                {field.recommendation.n_status && (
                    <NStatusChip nStatus={field.recommendation.n_status} />
                )}{" "}
            </div>
        );
    }

    if (!field.recommendation.insights_active) {
        return <div className="italic">{t("insights_inactive")}</div>;
    }

    if (field.recommendation.recent_application) {
        return (
            <div className="flex gap-1">
                <p>
                    {t("recent_application_on")}{" "}
                    {formatDateStringMMDDYYYY(
                        field.recommendation.recent_application,
                    )}
                </p>
            </div>
        );
    }
    if (field.recommendation.pending_application) {
        return <div>{t("pending_application")}</div>;
    }
    if (field.recommendation.gap) {
        return (
            <Chip
                classNames={{
                    base: "bg-gray-100",
                    content: "text-gray-900 font-bold",
                }}
                size="sm"
                radius="sm"
            >
                {t("image_gap")}
            </Chip>
        );
    }

    if (field.recommendation.recommend_fertigation) {
        return (
            <div className="flex gap-1">
                <Chip
                    classNames={{
                        base: "bg-red-100 font-bold",
                        content: "font-bold text-red-700",
                    }}
                    radius="sm"
                    size="sm"
                >
                    {t("apply_n")}
                </Chip>{" "}
                {field.recommendation.n_status && (
                    <NStatusChip nStatus={field.recommendation.n_status} />
                )}
            </div>
        );
    }

    return (
        <div className="flex gap-1">
            <Chip
                classNames={{
                    base: "bg-green-100 font-bold",
                    content: "font-bold text-green-700",
                }}
                radius="sm"
                size="sm"
            >
                {t("do_not_apply_n")}
            </Chip>
            {field.recommendation.n_status && (
                <NStatusChip nStatus={field.recommendation.n_status} />
            )}
        </div>
    );
}

function FieldListCard({
    field,
    isConsultantOrSentinel,
    userIsSentinel,
}: {
    field: Field;
    isConsultantOrSentinel: boolean;
    userIsSentinel: boolean;
}) {
    const { t } = useTranslation("fields-list");
    const location = useLocation();

    return (
        <Link
            to="/dashboard/fields/$fieldId/insights/ntime"
            params={{
                fieldId: field.id.toString(),
            }}
            search={{
                advisor: undefined,
                cameFrom: location.href.replace("/dashboard/", ""),
            }}
        >
            <Card className="hover:bg-gray-200">
                <CardHeader className="pb-1">
                    <div className="flex w-full justify-between">
                        <div className="flex flex-col">
                            <p className="font-semibold text-lg">
                                {field.name}
                            </p>
                            {isConsultantOrSentinel && (
                                <p className="text-sm">{field.grower}</p>
                            )}
                        </div>
                        <Chip
                            classNames={{
                                base: classNames({
                                    "bg-brandNavy-500 text-white":
                                        !!field.active_enrollment,
                                    "bg-gray-600 text-white":
                                        !field.active_enrollment,
                                }),
                            }}
                            size="sm"
                            radius="sm"
                        >
                            {getEnrollmentTierLabel(
                                field.active_enrollment?.enrollment_tier,
                                t,
                            )}
                        </Chip>
                    </div>
                </CardHeader>
                <CardFooter className="text-xs pt-0">
                    <div className="flex flex-row justify-between w-full">
                        <FooterContent field={field} />
                        {userIsSentinel && (
                            <div>
                                {field.service_provider?.name ?? t("na")} (
                                {field.service_provider?.account_manager
                                    ?.full_name ?? t("na")}
                                )
                            </div>
                        )}
                    </div>
                </CardFooter>
            </Card>
        </Link>
    );
}

function RouteComponent() {
    const { t } = useTranslation("fields-list");
    const { useGetRequest, axiosRequest } = useRequest();
    const navigate = Route.useNavigate();
    const {
        search,
        service_provider,
        grower,
        account_manager,
        enrollment_tier,
    } = Route.useSearch();

    const { setLayoutState } = useLayout();
    const { isMobile } = useIsMobile();
    useEffect(() => {
        setLayoutState("standard");
    }, [isMobile, setLayoutState]);

    const { isConsultantOrSentinel, userIsSentinel } = useAuth();
    const [searchInput, setSearchInput] = useState(search ?? "");

    useEffect(() => {
        setSearchInput(search ?? "");
    }, [search]);

    useEffect(() => {
        if (searchInput === (search ?? "")) return;

        const handler = setTimeout(() => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    search: searchInput === "" ? undefined : searchInput,
                }),
            });
        }, 700);

        return () => clearTimeout(handler);
    }, [navigate, search, searchInput]);

    const fieldsQuery = useInfiniteQuery<PaginationResponse<Field>>({
        queryKey: [
            "fields",
            "v2",
            search,
            service_provider,
            grower,
            account_manager,
            enrollment_tier,
        ],
        getNextPageParam: (lastPage) =>
            lastPage.next ? lastPage.page + 1 : undefined,
        getPreviousPageParam: (firstPage) =>
            firstPage.previous ? firstPage.page - 1 : undefined,
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<Field>>({
                url: "fields/v2/",
                params: {
                    search,
                    ordering: "name",
                    service_provider,
                    grower,
                    account_manager,
                    enrollment_tier,
                    page: pageParam,
                },
            });
        },
    });

    const fields = useMemo(() => {
        return fieldsQuery.data?.pages.flatMap((page) => page.results) ?? [];
    }, [fieldsQuery.data?.pages]);

    const parentRef = useRef<HTMLDivElement>(null);
    const virtualizer = useVirtualizer({
        count: fields.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 100,
        overscan: 10,
    });

    const items = virtualizer.getVirtualItems();

    const {
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage,
    } = fieldsQuery;
    useEffect(() => {
        const lastItem = items.at(-1);
        if (!lastItem) return;
        if (
            lastItem.index >= fields.length - 1 &&
            hasNextPage &&
            !isFetchingNextPage &&
            !isFetchNextPageError
        ) {
            void fetchNextPage();
        }
    }, [
        items,
        fields.length,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage,
    ]);

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        enabled: userIsSentinel,
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
        enabled: isConsultantOrSentinel,
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
        enabled: userIsSentinel,
    });

    const serviceProviders = useMemo(() => {
        if (!userIsSentinel) return [];
        return sortArrayByKey(serviceProvidersQuery.data ?? [], "name");
    }, [serviceProvidersQuery.data, userIsSentinel]);

    const accountManagers = useMemo(() => {
        if (!userIsSentinel) return [];
        return sortArrayByKey(accountManagersQuery.data ?? [], "full_name");
    }, [accountManagersQuery.data, userIsSentinel]);

    const farms = useMemo(() => {
        if (!isConsultantOrSentinel) return [];
        return sortArrayByKey(growersQuery.data ?? [], "name");
    }, [growersQuery.data, isConsultantOrSentinel]);

    const initialLoadError = fieldsQuery.isError && fields.length === 0;

    const {
        organizationMonitoringEnabled,
        irrigationEnabled,
        organizationPreviewEnabled,
    } = useFeatureFlag();

    const tierOptions = useMemo(() => {
        let options = TIER_OPTIONS;
        if (!organizationMonitoringEnabled) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Monitoring,
            );
        }
        if (!irrigationEnabled) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Irrigation,
            );
        }
        if (!organizationPreviewEnabled && !userIsSentinel) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Preview,
            );
        }
        return [{ tier: "all", label: "All" }, ...options];
    }, [
        organizationMonitoringEnabled,
        irrigationEnabled,
        organizationPreviewEnabled,
        userIsSentinel,
    ]);

    return (
        <div className="flex flex-col md:h-screen h-full">
            <div className="flex flex-row justify-between items-center gap-2 pr-2">
                <div className="flex flex-row gap-1 items-center p-2">
                    <Button
                        variant="light"
                        isIconOnly
                        size="lg"
                        as={Link}
                        href="/dashboard"
                        aria-label={t("back_to_dashboard")}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                    </Button>

                    <div className="font-bold text-3xl">{t("page_title")}</div>
                </div>
                <div>
                    <Button
                        as={Link}
                        to="/dashboard/fields/add"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                        color="primary"
                        size="sm"
                    >
                        {t("add_field")}
                    </Button>
                </div>
            </div>
            <div
                className={classNames(
                    "grid grid-cols-3 items-center gap-2 px-2 pb-2",
                    {
                        "grid-cols-2": !isConsultantOrSentinel,
                    },
                )}
            >
                <Input
                    placeholder={t("search_placeholder")}
                    value={searchInput}
                    onValueChange={setSearchInput}
                    isClearable
                    onClear={() => setSearchInput("")}
                    startContent={<FontAwesomeIcon icon={faSearch} />}
                    size="sm"
                />
                {userIsSentinel && (
                    <>
                        <Autocomplete
                            size="sm"
                            placeholder={t("account_manager_short")}
                            showScrollIndicators={false}
                            selectedKey={account_manager || null}
                            color={account_manager ? "primary" : "default"}
                            onSelectionChange={(key) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        account_manager: key
                                            ? key.toString()
                                            : "",
                                    }),
                                });
                            }}
                            aria-label={t("account_manager")}
                        >
                            {accountManagers.map((accountManager) => {
                                return (
                                    <AutocompleteItem
                                        key={accountManager.id}
                                        textValue={accountManager.full_name}
                                    >
                                        {accountManager.full_name}
                                    </AutocompleteItem>
                                );
                            })}
                        </Autocomplete>
                        <Autocomplete
                            size="sm"
                            placeholder={t("csp")}
                            showScrollIndicators={false}
                            selectedKey={service_provider || null}
                            color={service_provider ? "primary" : "default"}
                            onSelectionChange={(key) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        service_provider: key
                                            ? key.toString()
                                            : "",
                                    }),
                                });
                            }}
                            aria-label={t("csp")}
                        >
                            {serviceProviders.map((serviceProvider) => {
                                return (
                                    <AutocompleteItem
                                        key={serviceProvider.id}
                                        textValue={serviceProvider.name}
                                    >
                                        {serviceProvider.name}
                                    </AutocompleteItem>
                                );
                            })}
                        </Autocomplete>
                        <Autocomplete
                            size="sm"
                            placeholder="Tier"
                            showScrollIndicators={false}
                            selectedKey={enrollment_tier || null}
                            onSelectionChange={(key) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        enrollment_tier:
                                            key && key !== "all"
                                                ? key.toString()
                                                : undefined,
                                    }),
                                });
                            }}
                            color={enrollment_tier ? "primary" : "default"}
                            aria-label="Tier"
                        >
                            {tierOptions.map((option) => {
                                return (
                                    <AutocompleteItem
                                        key={option.tier}
                                        textValue={option.label}
                                    >
                                        {option.label}
                                    </AutocompleteItem>
                                );
                            })}
                        </Autocomplete>
                    </>
                )}
                {isConsultantOrSentinel && (
                    <Autocomplete
                        size="sm"
                        placeholder={t("farms")}
                        showScrollIndicators={false}
                        selectedKey={grower || null}
                        onSelectionChange={(key) => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    grower: key ? key.toString() : "",
                                }),
                            });
                        }}
                        color={grower ? "primary" : "default"}
                        aria-label={t("farms")}
                    >
                        {farms.map((farm) => {
                            return (
                                <AutocompleteItem
                                    key={farm.id}
                                    textValue={farm.name}
                                >
                                    {farm.name}
                                </AutocompleteItem>
                            );
                        })}
                    </Autocomplete>
                )}
            </div>

            <Divider />
            {fieldsQuery.isLoading ? (
                <div className="h-full w-full p-2">
                    <Skeleton className="h-full w-full rounded-lg " />
                </div>
            ) : initialLoadError ? (
                <div className="p-4 text-sm text-danger">
                    Failed to load fields.
                </div>
            ) : fields.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">
                    No fields found.
                </div>
            ) : (
                <div
                    ref={parentRef}
                    style={{
                        height: "100%",
                        width: "100%",
                        overflowY: "scroll",
                        contain: "strict",
                    }}
                >
                    <div
                        style={{
                            height: virtualizer.getTotalSize(),
                            width: "100%",
                            position: "relative",
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                transform: `translateY(${items[0]?.start ?? 0}px)`,
                            }}
                        >
                            {items.map((virtualRow) => (
                                <div
                                    key={virtualRow.key}
                                    data-index={virtualRow.index}
                                    ref={virtualizer.measureElement}
                                    className="p-1 pb-0"
                                >
                                    <FieldListCard
                                        userIsSentinel={userIsSentinel}
                                        field={
                                            fields[virtualRow.index]
                                        }
                                        isConsultantOrSentinel={
                                            isConsultantOrSentinel
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    {isFetchingNextPage ? (
                        <div className="flex justify-center py-4">
                            <Spinner color="primary" label="Loading fields..." />
                        </div>
                    ) : isFetchNextPageError ? (
                        <div className="flex justify-center py-4">
                            <Button
                                size="sm"
                                variant="light"
                                onPress={() => void fetchNextPage()}
                            >
                                Retry
                            </Button>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
}
